import Immutable from 'seamless-immutable';

import { SINGLE_SELECT_FILTERS } from 'constants/appConstants';
import { BRAND_NAME_FACET, COLOR_FACET, REAL_BRAND_FACET } from 'helpers/SearchUtils';
import type { FacetValueVariantType } from 'components/search/FacetAccordion/FacetAccordion.types';
import type { AutoCompleteDataType, FacetDataType, FacetValueType, NavigationItem, NavigationVersion } from 'types/search';

interface TransformOptions {
  facet: FacetDataType;
  autoComplete?: AutoCompleteDataType;
  navigationVersion?: NavigationVersion;
  shouldPinSelected?: boolean;
  isExpanded?: boolean;
}

interface TransformedFacetData {
  groupedValues: Record<string, FacetValueType[]>;
  sortedGroups: string[];
}

/**
 * Transforms facet values through a pipeline of filtering, grouping, and sorting operations.
 *
 * Pipeline steps:
 * 1. Use autocomplete values if available, otherwise use original facet values
 * 2. Filter single-select facets to show only selected value (if any)
 * 3. Group brand facets alphabetically, leave others ungrouped
 * 4. Sort groups with '#' (numbers) first, then alphabetically
 */
export function transformFacetValues({
  facet,
  autoComplete,
  navigationVersion,
  shouldPinSelected = false,
  isExpanded = false
}: TransformOptions): TransformedFacetData {
  const { facetField, values } = facet;

  // Step 1: Get base values (use autocomplete values if available, otherwise use original facet values)
  const baseValues = autoComplete?.[facetField]?.values ?? values;

  // Step 2: Apply facet-specific filtering only for single-select facets
  const filteredValues = applyFacetFiltering({ ...facet, values: baseValues }, navigationVersion);

  // Step 3: Pin selected values to the top (both sub-lists retain original backend sort order)
  const pinnedValues = shouldPinSelected ? pinSelectedValues(filteredValues) : filteredValues;

  // Step 4: Sort by count (collapsed) or alphabetically (expanded) when show more/less is active
  const sortedValues = shouldPinSelected ? sortFacetValues(pinnedValues, isExpanded) : pinnedValues;

  // Step 5: Group and sort
  const facetWithFilteredValues = { ...facet, values: sortedValues };
  const groupedValues = groupFacetValues(facetWithFilteredValues);
  const sortedGroups = sortGroupKeys({ keys: Object.keys(groupedValues) });

  return { groupedValues, sortedGroups };
}

/**
 * Pins selected values to the front of the list while preserving the original
 * backend sort order within each sub-list (selected and unselected).
 */
function pinSelectedValues(values: FacetValueType[]): FacetValueType[] {
  const selected = values.filter(v => v.selected);
  if (selected.length === 0 || selected.length === values.length) {
    return values;
  }
  const unselected = values.filter(v => !v.selected);
  return [...selected, ...unselected];
}

/**
 * Sorts facet values based on the expanded/collapsed state.
 * - Collapsed: sort by count descending (highest results first) so the top 10 are most relevant.
 * - Expanded: sort alphabetically by display name.
 * Selected values remain pinned at the top; sorting applies within each sub-list.
 */
function sortFacetValues(values: FacetValueType[], isExpanded: boolean): FacetValueType[] {
  const selected = values.filter(v => v.selected);
  const unselected = values.filter(v => !v.selected);

  const sortFn = isExpanded
    ? (a: FacetValueType, b: FacetValueType) => a.displayName.localeCompare(b.displayName)
    : (a: FacetValueType, b: FacetValueType) => (b.count ?? 0) - (a.count ?? 0);

  const sortedSelected = [...selected].sort(sortFn);
  const sortedUnselected = [...unselected].sort(sortFn);

  return selected.length > 0 ? [...sortedSelected, ...sortedUnselected] : sortedUnselected;
}

/**
 * Applies filtering logic specific to single-select facets.
 * For v2p2: shows all values (backend returns correct facetUrls).
 * For v2/legacy: shows only the selected value if one exists, otherwise shows all.
 * For multi-select facets: shows all values unchanged.
 */
function applyFacetFiltering(facet: FacetDataType, navigationVersion?: NavigationVersion): FacetValueType[] {
  if (isSingleSelectFacet(facet) && navigationVersion !== 'v2p2') {
    const selectedValue = facet.values.find(value => value.selected);
    return selectedValue ? [selectedValue] : facet.values;
  }
  return facet.values;
}

/**
 * Determines the UI variant (radio, checkbox, button, swatch) for a facet based on its properties.
 */
export function getFacetVariant(facet: FacetDataType, navigationVersion?: NavigationVersion): FacetValueVariantType {
  if (isSingleSelectFacet(facet)) {
    return 'radio';
  }

  if (facet.symbolicSizingField) {
    return 'button';
  }

  if (navigationVersion === 'v2p2' && facet.facetField === COLOR_FACET && facet.values.some(v => v.metadata?.imgUrl)) {
    return 'swatch';
  }

  return 'checkbox';
}

/**
 * Checks if a facet allows only single selection (e.g., Gender, Department).
 */
export function isSingleSelectFacet(facet: FacetDataType): boolean {
  return !!SINGLE_SELECT_FILTERS[facet.facetField as keyof typeof SINGLE_SELECT_FILTERS];
}

/**
 * Returns a comma-separated string of selected facet value names, sorted appropriately.
 * Returns null if no values are selected.
 */
export function getSelectedFacetNames(facet: NavigationItem): string | null {
  // For grouped facets, collect selected values from all children
  const allValues: FacetValueType[] = facet.type === 'group' ? facet.children.flatMap(child => child.values || []) : facet.values;

  const selectedValues = allValues.filter((value: FacetValueType) => value.selected).map((value: FacetValueType) => value.displayName);

  if (selectedValues.length === 0) {
    return null;
  }

  const mutableSelections = Immutable.asMutable(selectedValues);

  // Sort numerically if all values are numbers, otherwise sort alphabetically
  const sortedSelections = mutableSelections.sort((a, b) => {
    const numA = parseFloat(a);
    const numB = parseFloat(b);

    if (!isNaN(numA) && !isNaN(numB)) {
      return numA - numB;
    }

    return a.localeCompare(b);
  });

  return sortedSelections.join(', ');
}

/**
 * Returns the count of selected facet values.
 */
export function getSelectedFacetCount(facet: FacetDataType): number {
  return facet.values.filter(value => value.selected).length;
}

/**
 * Returns the facet values eligible for SEO link rendering.
 * Excludes sizing fields (symbolicSizingField) as they produce non-indexable pages.
 * For grouped facets, collects values from non-sizing children.
 */
export function getSeoLinkValues(facet: NavigationItem): FacetValueType[] {
  if (facet.type === 'group') {
    return facet.children.filter(child => !child.symbolicSizingField).flatMap(child => child.values);
  }
  return facet.symbolicSizingField ? [] : facet.values;
}

/**
 * Groups facet values for display purposes.
 * Brand facets are grouped alphabetically (A, B, C...) with numbers grouped under '#'.
 * All other facets remain ungrouped (single empty string key).
 */
export function groupFacetValues(facet: FacetDataType): Record<string, FacetValueType[]> {
  if (facet.facetField === BRAND_NAME_FACET || facet.facetField === REAL_BRAND_FACET) {
    return facet.values.reduce(
      (acc, value) => {
        const firstChar = value.displayName.charAt(0);
        const groupKey = /\d/.test(firstChar) ? '#' : firstChar.toUpperCase();
        if (!acc[groupKey]) acc[groupKey] = [];
        acc[groupKey]!.push(value);
        return acc;
      },
      {} as Record<string, FacetValueType[]>
    );
  }

  return { '': facet.values };
}

/**
 * Sorts group keys alphabetically, with '#' (numbers) always appearing first.
 */
export function sortGroupKeys({ keys }: { keys: string[] }): string[] {
  return keys.sort((a, b) => {
    if (a === '#') return -1;
    if (b === '#') return 1;
    return a.localeCompare(b);
  });
}
